// frontend/src/utils/printReport.js

export function generatePrintableCaseReport(caseData) {
  if (!caseData) return;

  const printWindow = window.open('', '_blank', 'width=900,height=800');
  if (!printWindow) {
    alert('Please allow popups to generate the printable case report.');
    return;
  }

  const clientName = caseData.client?.name || caseData.clientName || 'N/A';
  const productName = caseData.product?.name || caseData.productName || caseData.verificationType || 'Address Verification';
  const assignedToName = caseData.assignedTo?.name || caseData.assignedToName || 'Unassigned';
  const createdDate = caseData.createdAt ? new Date(caseData.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';
  const dueDate = caseData.dueDate ? new Date(caseData.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Verification Report - ${caseData.refNumber}</title>
  <style>
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
    }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      margin: 0;
      padding: 30px;
      color: #1e293b;
      background: #ffffff;
      line-height: 1.5;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 2px solid #2563eb;
      padding-bottom: 16px;
      margin-bottom: 24px;
    }
    .company-title {
      font-size: 24px;
      font-weight: 800;
      color: #1e3a8a;
      letter-spacing: -0.5px;
    }
    .company-sub {
      font-size: 11px;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .badge {
      display: inline-block;
      padding: 4px 12px;
      font-size: 11px;
      font-weight: 700;
      border-radius: 9999px;
      text-transform: uppercase;
      background: #eff6ff;
      color: #2563eb;
      border: 1px solid #bfdbfe;
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
      margin-bottom: 24px;
    }
    .card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 14px;
    }
    .card-title {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      color: #64748b;
      margin-bottom: 10px;
      letter-spacing: 0.5px;
      border-bottom: 1px solid #e2e8f0;
      padding-bottom: 4px;
    }
    .row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 6px;
      font-size: 13px;
    }
    .row .label {
      color: #64748b;
      font-weight: 500;
    }
    .row .val {
      color: #0f172a;
      font-weight: 600;
      text-align: right;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
      font-size: 13px;
    }
    th, td {
      border: 1px solid #e2e8f0;
      padding: 8px 12px;
      text-align: left;
    }
    th {
      background: #f1f5f9;
      color: #334155;
      font-weight: 600;
    }
    .remarks-box {
      background: #fffbeb;
      border: 1px solid #fef3c7;
      border-left: 4px solid #f59e0b;
      padding: 12px;
      border-radius: 4px;
      font-size: 13px;
      margin-top: 14px;
    }
    .footer {
      margin-top: 40px;
      padding-top: 16px;
      border-top: 1px solid #e2e8f0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 11px;
      color: #94a3b8;
    }
    .btn-print {
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 8px 18px;
      background: #2563eb;
      color: white;
      border: none;
      border-radius: 6px;
      font-weight: 600;
      cursor: pointer;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
  </style>
</head>
<body>
  <button class="btn-print no-print" onclick="window.print()">Print / Save as PDF</button>

  <div class="header">
    <div>
      <div class="company-title">AD VENTURES</div>
      <div class="company-sub">Field Verification & Risk Intelligence</div>
    </div>
    <div style="text-align: right;">
      <span class="badge">${(caseData.status || 'VERIFICATION').toUpperCase()}</span>
      <div style="font-size: 13px; font-weight: 700; color: #0f172a; margin-top: 6px;">Ref: ${caseData.refNumber}</div>
      <div style="font-size: 11px; color: #64748b;">Generated: ${new Date().toLocaleString('en-IN')}</div>
    </div>
  </div>

  <div class="grid">
    <!-- Applicant Information -->
    <div class="card">
      <div class="card-title">Applicant Details</div>
      <div class="row"><span class="label">Name:</span><span class="val">${caseData.applicantName || 'N/A'}</span></div>
      <div class="row"><span class="label">Phone:</span><span class="val">${caseData.applicantPhone || 'N/A'}</span></div>
      <div class="row"><span class="label">Email:</span><span class="val">${caseData.applicantEmail || 'N/A'}</span></div>
      <div class="row"><span class="label">Address:</span><span class="val" style="max-width: 260px;">${caseData.address || 'N/A'}</span></div>
    </div>

    <!-- Case Operational Details -->
    <div class="card">
      <div class="card-title">Case Metadata</div>
      <div class="row"><span class="label">Client:</span><span class="val">${clientName}</span></div>
      <div class="row"><span class="label">Verification Type:</span><span class="val">${productName}</span></div>
      <div class="row"><span class="label">Assigned Executive:</span><span class="val">${assignedToName}</span></div>
      <div class="row"><span class="label">Priority:</span><span class="val">${(caseData.priority || 'Medium').toUpperCase()}</span></div>
      <div class="row"><span class="label">Creation Date:</span><span class="val">${createdDate}</span></div>
      <div class="row"><span class="label">Due Date:</span><span class="val">${dueDate}</span></div>
    </div>
  </div>

  <!-- Geo Coordinates -->
  ${caseData.latitude || caseData.mapCoords ? `
  <div class="card" style="margin-bottom: 24px;">
    <div class="card-title">GPS & Location Telemetry</div>
    <div class="row">
      <span class="label">Coordinates:</span>
      <span class="val">${caseData.latitude || caseData.mapCoords?.latitude || 'N/A'}, ${caseData.longitude || caseData.mapCoords?.longitude || 'N/A'}</span>
    </div>
    <div class="row">
      <span class="label">Google Maps Link:</span>
      <span class="val"><a href="https://maps.google.com/?q=${caseData.latitude || caseData.mapCoords?.latitude},${caseData.longitude || caseData.mapCoords?.longitude}" target="_blank">View Satellite Location</a></span>
    </div>
  </div>
  ` : ''}

  <!-- Verification Answers / Questions -->
  ${caseData.answers && caseData.answers.length > 0 ? `
  <div class="card" style="margin-bottom: 24px;">
    <div class="card-title">Verification Checklist & Question Responses</div>
    <table>
      <thead>
        <tr>
          <th>Verification Parameter</th>
          <th>FE Response</th>
        </tr>
      </thead>
      <tbody>
        ${caseData.answers.map(ans => `
          <tr>
            <td style="font-weight: 500;">${ans.question || ans.label || 'Question'}</td>
            <td style="font-weight: 600; color: #1e293b;">${ans.answer || ans.value || 'Yes'}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  </div>
  ` : ''}

  <!-- Remarks -->
  <div class="remarks-box">
    <strong>Executive & QC Observations:</strong><br />
    ${caseData.remarks?.feRemark || caseData.feRemark ? `<div><strong>Field Remark:</strong> ${caseData.remarks?.feRemark || caseData.feRemark}</div>` : ''}
    ${caseData.remarks?.qcRemark || caseData.qcRemark ? `<div><strong>QC Remark:</strong> ${caseData.remarks?.qcRemark || caseData.qcRemark}</div>` : ''}
    ${caseData.remarks?.finalRemark || caseData.finalRemark ? `<div><strong>Final Decision:</strong> ${caseData.remarks?.finalRemark || caseData.finalRemark}</div>` : ''}
    ${!caseData.feRemark && !caseData.qcRemark && !caseData.remarks ? '<div>No remarks submitted yet.</div>' : ''}
  </div>

  <div class="footer">
    <div>Ad Ventures Operations System • Confidential Verification Document</div>
    <div>Page 1 of 1</div>
  </div>

  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print();
      }, 500);
    };
  </script>
</body>
</html>
  `;

  if (printWindow.document && printWindow.document.open) {
    printWindow.document.open();
  }
  if (printWindow.document && printWindow.document.write) {
    printWindow.document.write(html);
  }
  if (printWindow.document && printWindow.document.close) {
    printWindow.document.close();
  }
}

export function generatePrintableDocument(doc, caseData) {
  const printWindow = window.open('', '_blank', 'width=850,height=800');
  if (!printWindow) {
    alert('Please allow popups to view/print document.');
    return;
  }

  const docName = doc?.name || 'Verification_Document.pdf';
  const ref = caseData?.refNumber || 'ADV-CASE';
  const applicant = caseData?.applicantName || 'Applicant';
  const client = caseData?.client?.name || caseData?.clientName || 'Partner Bank';

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${docName} - ${ref}</title>
  <style>
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
    }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      margin: 0;
      padding: 40px;
      color: #1e293b;
      background: #f8fafc;
    }
    .sheet {
      max-width: 750px;
      margin: 0 auto;
      background: #ffffff;
      padding: 40px;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
      position: relative;
    }
    .watermark {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-35deg);
      font-size: 54px;
      font-weight: 900;
      color: rgba(37, 99, 235, 0.06);
      pointer-events: none;
      white-space: nowrap;
      text-transform: uppercase;
    }
    .header {
      border-bottom: 2px solid #2563eb;
      padding-bottom: 16px;
      margin-bottom: 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .title {
      font-size: 20px;
      font-weight: 800;
      color: #1e3a8a;
    }
    .badge {
      background: #dcfce7;
      color: #15803d;
      border: 1px solid #86efac;
      padding: 4px 10px;
      border-radius: 9999px;
      font-size: 11px;
      font-weight: 700;
    }
    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      background: #f1f5f9;
      padding: 16px;
      border-radius: 6px;
      font-size: 13px;
      margin-bottom: 24px;
    }
    .doc-preview-area {
      border: 2px dashed #94a3b8;
      border-radius: 8px;
      padding: 30px;
      text-align: center;
      background: #fafafa;
      margin-bottom: 24px;
    }
    .stamp {
      display: inline-block;
      border: 2px solid #16a34a;
      color: #16a34a;
      padding: 6px 14px;
      font-size: 12px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 1px;
      border-radius: 4px;
      margin-top: 10px;
    }
    .btn-print {
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 8px 18px;
      background: #2563eb;
      color: white;
      border: none;
      border-radius: 6px;
      font-weight: 600;
      cursor: pointer;
    }
  </style>
</head>
<body>
  <button class="btn-print no-print" onclick="window.print()">Print / Save PDF</button>
  <div class="sheet">
    <div class="watermark">AD VENTURES VERIFIED</div>
    <div class="header">
      <div>
        <div class="title">OFFICIAL VERIFICATION DOCUMENT</div>
        <div style="font-size: 11px; color: #64748b; text-transform: uppercase;">Ad Ventures Risk & Audit Division</div>
      </div>
      <span class="badge">AUDIT VERIFIED</span>
    </div>

    <div class="info-grid">
      <div><strong>Document:</strong> ${docName}</div>
      <div><strong>Case Reference:</strong> ${ref}</div>
      <div><strong>Applicant:</strong> ${applicant}</div>
      <div><strong>Institutional Client:</strong> ${client}</div>
      <div><strong>Verified On:</strong> ${new Date().toLocaleDateString('en-IN')}</div>
      <div><strong>Security Check:</strong> KYC Integrity Compliant</div>
    </div>

    <div class="doc-preview-area">
      <div style="font-size: 40px; margin-bottom: 10px;">📄</div>
      <div style="font-size: 16px; font-weight: 700; color: #1e293b;">${docName}</div>
      <div style="font-size: 12px; color: #64748b; margin-top: 4px;">File format: PDF Document • Original Uploaded Copy</div>
      <div class="stamp">✓ KYC & Field Verified</div>
    </div>

    <div style="font-size: 11px; color: #64748b; border-top: 1px solid #e2e8f0; padding-top: 14px; display: flex; justify-content: space-between;">
      <span>This document was reviewed as part of verification reference ${ref}.</span>
      <span>Confidential Record</span>
    </div>
  </div>
</body>
</html>
  `;

  if (printWindow.document && printWindow.document.open) printWindow.document.open();
  if (printWindow.document && printWindow.document.write) printWindow.document.write(html);
  if (printWindow.document && printWindow.document.close) printWindow.document.close();
}

export function generatePrintableInvoice(invoice) {
  const printWindow = window.open('', '_blank', 'width=850,height=800');
  if (!printWindow) {
    alert('Please allow popups to view/print invoice.');
    return;
  }

  const num = invoice?.invoiceNumber || invoice?.id || 'INV-2026-001';
  const client = invoice?.client?.name || invoice?.clientName || 'Partner Client';
  const amount = Number(invoice?.amount || 15400).toLocaleString('en-IN');
  const date = invoice?.createdAt || new Date().toISOString().slice(0, 10);
  const status = (invoice?.status || 'PAID').toUpperCase();

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Tax Invoice - ${num}</title>
  <style>
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
    }
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #1e293b; }
    .sheet { max-width: 750px; margin: 0 auto; background: #fff; padding: 36px; border: 1px solid #e2e8f0; border-radius: 8px; }
    .header { display: flex; justify-content: space-between; border-bottom: 2px solid #2563eb; padding-bottom: 16px; margin-bottom: 24px; }
    .company { font-size: 24px; font-weight: 800; color: #1e3a8a; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 13px; }
    th, td { border: 1px solid #e2e8f0; padding: 10px 14px; text-align: left; }
    th { background: #f8fafc; font-weight: 700; color: #475569; }
    .total-box { margin-top: 20px; text-align: right; font-size: 16px; font-weight: 800; color: #0f172a; }
    .btn-print { position: fixed; top: 20px; right: 20px; padding: 8px 18px; background: #2563eb; color: white; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; }
  </style>
</head>
<body>
  <button class="btn-print no-print" onclick="window.print()">Print / Download PDF</button>
  <div class="sheet">
    <div class="header">
      <div>
        <div class="company">AD VENTURES</div>
        <div style="font-size: 11px; color: #64748b;">TAX INVOICE & BILLING SUMMARY</div>
      </div>
      <div style="text-align: right;">
        <div style="font-size: 16px; font-weight: 800; color: #2563eb;">${num}</div>
        <div style="font-size: 12px; color: #64748b;">Date: ${date}</div>
        <div style="font-size: 12px; font-weight: 700; color: #16a34a; margin-top: 4px;">${status}</div>
      </div>
    </div>
    <div style="font-size: 13px; line-height: 1.6; margin-bottom: 20px;">
      <strong>Billed To:</strong><br />
      ${client}<br />
      Corporate Banking & Risk Governance Unit
    </div>
    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th style="text-align: center;">Cases</th>
          <th style="text-align: right;">Base Rate</th>
          <th style="text-align: right;">Total Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Physical Address Verification Services</td>
          <td style="text-align: center;">48</td>
          <td style="text-align: right;">₹ 250</td>
          <td style="text-align: right;">₹ 12,000</td>
        </tr>
        <tr>
          <td>Urgent / Outstation Verification Premium</td>
          <td style="text-align: center;">8</td>
          <td style="text-align: right;">₹ 350</td>
          <td style="text-align: right;">₹ 2,800</td>
        </tr>
        <tr>
          <td>GST (18% Integrated Goods & Service Tax)</td>
          <td style="text-align: center;">-</td>
          <td style="text-align: right;">18%</td>
          <td style="text-align: right;">₹ 600</td>
        </tr>
      </tbody>
    </table>
    <div class="total-box">
      Net Payable Amount: ₹ ${amount}
    </div>
    <div style="margin-top: 30px; font-size: 11px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 14px;">
      Payment Remittance: HDFC Bank Current A/c No. 50200049281734 | IFSC: HDFC0000124 | AD Ventures Private Limited
    </div>
  </div>
</body>
</html>
  `;

  if (printWindow.document && printWindow.document.open) printWindow.document.open();
  if (printWindow.document && printWindow.document.write) printWindow.document.write(html);
  if (printWindow.document && printWindow.document.close) printWindow.document.close();
}

export function generatePrintableIdCard(user) {
  const printWindow = window.open('', '_blank', 'width=750,height=700');
  if (!printWindow) {
    alert('Please allow popups to view/print ID Card.');
    return;
  }

  const name = user?.name || 'Field Executive';
  const role = (user?.role || 'field_executive').toUpperCase().replace('_', ' ');
  const id = user?.id || 'USR005';
  const phone = user?.phone || '+91 99887 76655';
  const avatar = user?.avatar || 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200';

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Official ID Card - ${name}</title>
  <style>
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
    }
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: #f1f5f9; padding: 40px; display: flex; flex-direction: column; align-items: center; }
    .card { width: 340px; background: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 10px 25px -5px rgba(0,0,0,0.15); border: 1px solid #cbd5e1; }
    .card-header { background: linear-gradient(135deg, #1e3a8a, #2563eb); color: #fff; padding: 20px; text-align: center; }
    .photo-area { display: flex; justify-content: center; margin-top: -35px; }
    .photo { width: 80px; height: 80px; border-radius: 50%; border: 4px solid #fff; object-fit: cover; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
    .body { padding: 20px; text-align: center; font-size: 13px; color: #334155; }
    .name { font-size: 18px; font-weight: 800; color: #0f172a; margin-top: 8px; }
    .role-badge { display: inline-block; background: #eff6ff; color: #1d4ed8; padding: 4px 12px; border-radius: 9999px; font-weight: 700; font-size: 11px; margin-top: 4px; border: 1px solid #bfdbfe; }
    .details { margin-top: 16px; text-align: left; font-size: 12px; background: #f8fafc; padding: 12px; border-radius: 8px; }
    .details-row { display: flex; justify-content: space-between; margin-bottom: 6px; }
    .footer { background: #0f172a; color: #94a3b8; font-size: 10px; text-align: center; padding: 10px; }
    .btn-print { margin-bottom: 20px; padding: 8px 18px; background: #2563eb; color: white; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; }
  </style>
</head>
<body>
  <button class="btn-print no-print" onclick="window.print()">Print / Save ID Card</button>
  <div class="card">
    <div class="card-header">
      <div style="font-size: 18px; font-weight: 900; letter-spacing: -0.5px;">AD VENTURES</div>
      <div style="font-size: 10px; letter-spacing: 1px; text-transform: uppercase; opacity: 0.9;">Authorized Field Personnel</div>
    </div>
    <div class="photo-area">
      <img src="${avatar}" class="photo" alt="${name}" />
    </div>
    <div class="body">
      <div class="name">${name}</div>
      <span class="role-badge">${role}</span>
      <div class="details">
        <div class="details-row"><span>Personnel ID:</span><strong>${id}</strong></div>
        <div class="details-row"><span>Phone:</span><strong>${phone}</strong></div>
        <div class="details-row"><span>Valid Thru:</span><strong>31 Dec 2026</strong></div>
        <div class="details-row"><span>Authority:</span><strong>Field Verification</strong></div>
      </div>
    </div>
    <div class="footer">
      Official AD Ventures Identity • Property of Risk Verification Operations
    </div>
  </div>
</body>
</html>
  `;

  if (printWindow.document && printWindow.document.open) printWindow.document.open();
  if (printWindow.document && printWindow.document.write) printWindow.document.write(html);
  if (printWindow.document && printWindow.document.close) printWindow.document.close();
}

export function generatePrintableAuthLetter(user, clientName = 'HDFC Bank') {
  const printWindow = window.open('', '_blank', 'width=850,height=800');
  if (!printWindow) {
    alert('Please allow popups to view/print Authorization Letter.');
    return;
  }

  const name = user?.name || 'Field Executive';
  const id = user?.id || 'USR005';

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Authority Letter - ${name}</title>
  <style>
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
    }
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #1e293b; background: #ffffff; line-height: 1.6; }
    .sheet { max-width: 750px; margin: 0 auto; border: 1px solid #cbd5e1; padding: 40px; border-radius: 8px; }
    .header { border-bottom: 2px solid #059669; padding-bottom: 16px; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: center; }
    .btn-print { position: fixed; top: 20px; right: 20px; padding: 8px 18px; background: #059669; color: white; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; }
  </style>
</head>
<body>
  <button class="btn-print no-print" onclick="window.print()">Print / Save Authority Letter</button>
  <div class="sheet">
    <div class="header">
      <div>
        <div style="font-size: 22px; font-weight: 800; color: #065f46;">AD VENTURES OPERATIONS</div>
        <div style="font-size: 11px; color: #64748b;">OFFICIAL FIELD VERIFICATION AUTHORIZATION</div>
      </div>
      <div style="font-size: 12px; font-weight: 700; color: #047857; text-align: right;">
        Ref: ADV-AUTH-2026/09<br />Date: ${new Date().toLocaleDateString('en-IN')}
      </div>
    </div>
    <p><strong>TO WHOMSOEVER IT MAY CONCERN</strong></p>
    <p>This is to certify that <strong>${name}</strong> (Employee ID: <strong>${id}</strong>) is an authorized verification representative of AD Ventures Operations, officially mandated to conduct physical address, residency, and document verification checks on behalf of our institutional partner <strong>${clientName}</strong>.</p>
    <p>We kindly request the occupants, property management, society authorities, and applicants to cooperate with the bearer of this letter to ensure prompt completion of mandatory background verification formalities.</p>
    <div style="margin-top: 40px; display: flex; justify-content: space-between; align-items: flex-end;">
      <div>
        <div style="font-size: 12px; color: #64748b;">Bearer Signature</div>
        <div style="border-bottom: 1px solid #94a3b8; width: 180px; height: 35px;"></div>
        <div style="font-size: 11px; margin-top: 4px;">${name}</div>
      </div>
      <div style="text-align: right;">
        <div style="font-size: 12px; color: #059669; font-weight: 700;">✓ Digitally Certified & Seal Authorized</div>
        <div style="border-bottom: 1px solid #94a3b8; width: 180px; height: 35px; margin-left: auto;"></div>
        <div style="font-size: 11px; margin-top: 4px;">Head of Operations, AD Ventures</div>
      </div>
    </div>
  </div>
</body>
</html>
  `;

  if (printWindow.document && printWindow.document.open) printWindow.document.open();
  if (printWindow.document && printWindow.document.write) printWindow.document.write(html);
  if (printWindow.document && printWindow.document.close) printWindow.document.close();
}

